import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useLocale from 'hooks/useLocale';

import { decreaseActiveMapsCount, increaseActiveMapsCount } from 'stores/mapsConfigSlice';

import * as S from './styled/Map.styled';

/**
 * @description Map base component
 */

const defaultConfig = { lat: 24.69664246, lng: 46.6827321, zoom: 11 };
const Maps = ({ setMap, zoom, fullscreenControl, defaultCountry, mapTypeControl, customMapSettings }) => {
	const dispatch = useDispatch();
	const [mapRef, setMapRef] = useState(null);
	const { Country, MapSettings } = useLocale();

	const { darkMode } = useSelector((state) => state.layout);
	const user = useSelector((state) => state.user);
	const { installed, loading } = useSelector((state) => state.mapsConfig);
	const config = useMemo(() => {
		if (customMapSettings) {
			return customMapSettings;
		}

		if (!user?.initialized) {
			return defaultConfig;
		}

		if (defaultCountry === Country) {
			return MapSettings ?? defaultConfig;
		}
		const searchValue = defaultCountry || 'SA';
		const selectedCountry = user?.generalSettings?.countries?.find(
			(country) => country?.name === searchValue || country?.ISO2 === searchValue
		);

		return JSON.parse(selectedCountry?.mapSettings ?? JSON.stringify(MapSettings ?? defaultConfig));
	}, [user, MapSettings, defaultCountry]);

	const loadMap = useCallback(() => {
		if (setMap) {
			const maps = window.google.maps;
			setMap(
				new maps.Map(mapRef, {
					center: { lat: config?.lat, lng: config?.lng },
					mapId: darkMode ? import.meta.env.VITE_MAP_ID_DARK : import.meta.env.VITE_MAP_ID_LIGHT,
					streetViewControl: false,
					fullscreenControl: fullscreenControl ?? false,
					mapTypeControl: mapTypeControl ?? false,
					zoom: zoom ?? config?.zoom ?? 8,
					minZoom: 3,
					restriction: {
						// restrict map to the world
						latLngBounds: {
							north: 80,
							south: -80,
							west: -179.999999,
							east: 179.999999
						},
						strictBounds: true
					}
				})
			);
		}
	}, [Country, MapSettings, darkMode, mapRef, setMap, zoom, defaultCountry, mapTypeControl]);

	useEffect(() => {
		dispatch(increaseActiveMapsCount());

		return () => {
			dispatch(decreaseActiveMapsCount());
		};
	}, []);

	useEffect(() => {
		if (installed && !loading && mapRef && window.google?.maps) {
			loadMap();
		} else {
			setMap?.(null);
		}
	}, [installed, mapRef, loading, defaultCountry]);

	return (
		<S.MapContainer>
			{installed && <div ref={setMapRef} style={{ width: '100%', height: '100%' }} />}
		</S.MapContainer>
	);
};

export default Maps;
